// FILE: src/components/BadgeCallout.js
//
// BadgeCallout — the rendering for every badge in the system. It is image-first
// with a graceful text fallback:
//
//   • If the badge supplies an `assetKey` and the art loads, the PNG/SVG sticker
//     is rendered in place of the capsule (the art fully controls the look).
//   • If there is no `assetKey`, or the file is missing / 404s, it renders the
//     ONE canonical capsule: a rounded pill with a category dot + uppercase mono
//     label, coloured by category.
//
//     <BadgeCallout badge={productBadge} />            // image if asset exists
//     <BadgeCallout badge={telemetryBadge} animate="pulse" />
//
// Purely presentational — pass it a resolved badge config (from
// src/config/badges.js via badgeService) and it renders. It knows nothing about
// exams, learners, or routing.
//
// Assets are served from /themes/pm-pixel/badges/<assetKey>[.png] (theme-shaped,
// matching CoachAvatar / AchievementBadge / cert-logos, so the white-label Phase
// 2 layer can later swap the base path with no call-site change). Drop a file
// named to match a badge's `assetKey` in that folder and it lights up — no code
// change. `assetKey` may include an explicit extension (e.g. 'foo.svg'); without
// one, '.png' is assumed.
//
// Colour comes from BADGE_COLORS via getBadgeColors() and is applied as CSS
// custom properties so this component hardcodes NO hue. Motion is subtle
// ('fade' default, 'pulse' for earned telemetry) and disabled under
// prefers-reduced-motion (in the CSS module). No Redux. Hooks only.

import React from 'react';
import { getBadgeColors } from '../../config/badges';
import styles from './BadgeCallout.module.css';

// Theme stand-in (mirrors CertificationBadge). In the white-label phase this
// base path comes from the active theme manifest.
const BADGE_BASE_PATH = '/themes/pm-pixel/badges/';

function resolveAssetSrc(assetKey) {
  const hasExt = /\.(png|svg|webp|jpe?g)$/i.test(assetKey);
  return BADGE_BASE_PATH + assetKey + (hasExt ? '' : '.png');
}

/**
 * @param {object}  badge            resolved badge config { id,label,type,alt,assetKey?,... }.
 *                                   When falsy / unlabelled the component renders nothing.
 * @param {'fade'|'pulse'} [animate='fade']  entrance / idle motion.
 * @param {'sm'|'md'} [size='md']    capsule size (text mode; responsive within each).
 * @param {string}  [className='']   extra class on the wrapper.
 */
export default function BadgeCallout({
  badge,
  animate = 'fade',
  size = 'md',
  className = '',
}) {
  // If the art 404s / fails to decode, swap to the text capsule rather than
  // showing a broken-image glyph — the card always renders something sensible.
  const [imgFailed, setImgFailed] = React.useState(false);

  if (!badge || !badge.label) return null;

  const accessibleName = badge.alt || badge.label;
  const motionClass = animate === 'pulse' ? styles.pulse : styles.fade;

  // ── Image mode — a provided asset replaces the capsule entirely ────────────
  // `imageSrc` (an absolute URL, e.g. an admin-uploaded asset in Supabase
  // Storage) takes precedence; otherwise a themed `assetKey` resolves to the
  // bundled /themes path. Either way, a load failure falls back to the capsule.
  const imageSrc = badge.imageSrc || (badge.assetKey ? resolveAssetSrc(badge.assetKey) : null);
  if (imageSrc && !imgFailed) {
    const wrapperClass = [styles.imgBadge, size === 'lg' ? styles.imgLg : '', motionClass, className].filter(Boolean).join(' ');
    return (
      <span
        className={wrapperClass}
        role="img"
        aria-label={accessibleName}
        data-testid="badge-callout"
        data-badge-id={badge.id}
        data-badge-type={badge.type}
        data-asset={badge.assetKey || undefined}
      >
        <img
          className={styles.img}
          src={imageSrc}
          alt=""
          loading="lazy"
          decoding="async"
          draggable="false"
          onError={() => setImgFailed(true)}
        />
      </span>
    );
  }

  // ── Text capsule mode (default, and fallback when art is missing) ──────────
  const colors = getBadgeColors(badge);
  // Colour comes in as CSS custom properties — the module styles consume them,
  // so no colour literal lives in the component or the stylesheet.
  const styleVars = {
    '--badge-bg': colors.bg,
    '--badge-border': colors.border,
    '--badge-text': colors.text,
    '--badge-dot': colors.dot,
  };
  const cls = [
    styles.badge,
    styles[size] || styles.md,
    motionClass,
    className,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <span
      className={cls}
      style={styleVars}
      role="img"
      aria-label={accessibleName}
      data-testid="badge-callout"
      data-badge-id={badge.id}
      data-badge-type={badge.type}
      data-fallback={badge.assetKey || badge.imageSrc ? 'true' : undefined}
    >
      <span className={styles.dot} aria-hidden="true" />
      <span className={styles.label}>{badge.label}</span>
    </span>
  );
}
